'use client';

import { cn } from 'cn';
import { motion, useReducedMotion } from 'motion/react';
interface ClaiOrbProps {
  /** Rendered diameter in px. */
  size?: number;
  /** Changing this plays a short "heard you" pulse. */
  pulseKey?: string | number;
  /** Faster, brighter motion while Clai is working. */
  thinking?: boolean;
  className?: string;
}

// All-green palette: mint, emerald and lime swirl inside the tile.
const LAYERS = [{
  gradient: 'radial-gradient(circle at 30% 30%, #ecfdf5 0%, #6ee7b7 40%, transparent 70%)',
  duration: 6,
  offset: {
    x: -10,
    y: -8
  }
}, {
  gradient: 'radial-gradient(circle at 70% 40%, #dcfce7 0%, #4ade80 42%, transparent 70%)',
  duration: 7.5,
  offset: {
    x: 10,
    y: 4
  }
}, {
  gradient: 'radial-gradient(circle at 50% 75%, #f7fee7 0%, #a3e635 38%, transparent 68%)',
  duration: 9,
  offset: {
    x: -4,
    y: 10
  }
}] as const;

/** Rounded-square corners, echoing the Clai logo tile. */
const TILE_RADIUS = '30%';
const LOOP = Number.POSITIVE_INFINITY;

/**
 * Clai's "presence": a green, rounded-square tile that floats, tilts and
 * swirls so the product feels alive while you set it up.
 */
export function ClaiOrb({
  size = 72,
  pulseKey,
  thinking = false,
  className
}: ClaiOrbProps) {
  const reduceMotion = useReducedMotion();
  const speed = thinking ? 0.5 : 1;
  const loop = (duration: number) => ({
    duration: duration * speed,
    repeat: LOOP,
    ease: 'easeInOut' as const
  });
  return <div aria-hidden className={cn('relative shrink-0', className)} style={{
    width: size,
    height: size
  }} data-sentry-component="ClaiOrb" data-sentry-source-file="clai-orb.tsx">
      {/* "Heard you" pop whenever pulseKey changes. */}
      <motion.div key={pulseKey} className="absolute inset-0" initial={reduceMotion ? false : {
      scale: 0.8,
      rotate: -8
    }} animate={{
      scale: 1,
      rotate: 0
    }} transition={{
      type: 'spring',
      stiffness: 280,
      damping: 12
    }} data-sentry-element="motion.div" data-sentry-source-file="clai-orb.tsx">
        {/* Float and tilt */}
        <motion.div className="absolute inset-0" animate={reduceMotion ? undefined : {
        y: [0, -size * 0.08, 0, size * 0.04, 0],
        rotate: [0, 6, 0, -6, 0]
      }} transition={loop(4.5)} data-sentry-element="motion.div" data-sentry-source-file="clai-orb.tsx">
          {/* Green glow */}
          <motion.div className="absolute -inset-[35%] blur-2xl" style={{
          borderRadius: TILE_RADIUS,
          background: 'radial-gradient(circle, rgba(34,197,94,0.55) 0%, rgba(132,204,22,0.25) 45%, transparent 70%)'
        }} animate={reduceMotion ? undefined : {
          opacity: thinking ? [0.7, 1, 0.7] : [0.45, 0.85, 0.45],
          scale: [0.95, 1.08, 0.95]
        }} transition={loop(2.4)} data-sentry-element="motion.div" data-sentry-source-file="clai-orb.tsx" />
          {/* Tile */}
          <motion.div className="absolute inset-0 overflow-hidden bg-green-50 shadow-[inset_0_0_12px_rgba(255,255,255,0.8),0_6px_18px_-6px_rgba(22,163,74,0.55)]" style={{
          borderRadius: TILE_RADIUS
        }} animate={reduceMotion ? undefined : {
          scale: [1, 1.07, 0.98, 1.04, 1]
        }} transition={loop(3)} data-sentry-element="motion.div" data-sentry-source-file="clai-orb.tsx">
            {LAYERS.map(layer => <motion.div key={layer.gradient} className="absolute -inset-[25%] rounded-full opacity-90 mix-blend-multiply" style={{
            background: layer.gradient
          }} animate={reduceMotion ? undefined : {
            rotate: 360,
            x: [0, layer.offset.x, 0, -layer.offset.x, 0],
            y: [0, layer.offset.y, 0, -layer.offset.y, 0]
          }} transition={{
            rotate: {
              duration: layer.duration * speed,
              repeat: LOOP,
              ease: 'linear'
            },
            x: loop(layer.duration / 2),
            y: loop(layer.duration / 3)
          }} />)}
            {/* Sheen sweeping across */}
            <motion.div className="absolute inset-y-0 -left-full w-full bg-linear-to-r from-transparent via-white/60 to-transparent" style={{
            skewX: -20
          }} animate={reduceMotion ? undefined : {
            x: ['0%', '300%']
          }} transition={{
            duration: 1.6 * speed,
            repeat: LOOP,
            repeatDelay: 1.8 * speed,
            ease: 'easeInOut'
          }} data-sentry-element="motion.div" data-sentry-source-file="clai-orb.tsx" />
            {/* Glassy highlight */}
            <div className="absolute top-[10%] left-[14%] h-[24%] w-[42%] rounded-full bg-white/70 blur-[5px]" />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>;
}

/** Drifting color fields behind the signup cards. */
export function AmbientBackground({
  className
}: {
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  const blobs = [{
    color: 'rgba(134,239,172,0.55)',
    className: '-top-32 -left-24 h-[480px] w-[480px]',
    move: {
      x: [0, 60, 0],
      y: [0, 40, 0]
    },
    duration: 22
  }, {
    color: 'rgba(103,232,249,0.45)',
    className: '-right-32 -bottom-40 h-[560px] w-[560px]',
    move: {
      x: [0, -50, 0],
      y: [0, -30, 0]
    },
    duration: 26
  }, {
    color: 'rgba(253,224,71,0.25)',
    className: 'top-1/3 right-1/4 h-[320px] w-[320px]',
    move: {
      x: [0, -40, 30, 0],
      y: [0, 30, -20, 0]
    },
    duration: 30
  }];
  return <div aria-hidden className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)} data-sentry-component="AmbientBackground" data-sentry-source-file="clai-orb.tsx">
      {blobs.map(blob => <motion.div key={blob.className} className={cn('absolute rounded-full blur-3xl', blob.className)} style={{
      background: `radial-gradient(circle, ${blob.color} 0%, transparent 70%)`
    }} animate={reduceMotion ? undefined : blob.move} transition={{
      duration: blob.duration,
      repeat: Number.POSITIVE_INFINITY,
      ease: 'easeInOut'
    }} />)}
    </div>;
}